/**
 * ``useEcriturePreview`` — the in-pane read-only preview of one past version
 * (F-VERSIONS-PREVIEW). `open(ecritureId)` fetches the écriture's full detail
 * via the `caseList.getEcriture` port; `close()` clears it. Pure orchestration
 * (W1), no Office.js (W2).
 *
 * Mirrors `use-versions.ts`: reads the port from `getContainer()`, wraps a
 * rejected call into a typed `error` + `console.error`, never throws. No new
 * Zustand store (W5) — this is per-preview ephemeral UI state, so local hook
 * state is the right home (the same posture as a modal's open/close).
 */

import { useCallback, useState } from 'react'
import { getContainer } from '@/container'
import type { EcritureDetail } from '@/domain/ecriture/entity'

export interface UseEcriturePreviewResult {
  detail: EcritureDetail | null
  loading: boolean
  error: Error | null
  /** Fetch + show one écriture's detail. Replaces any current preview. */
  open: (ecritureId: string) => Promise<void>
  /** Clear the preview (back to the history list). */
  close: () => void
}

export function useEcriturePreview(): UseEcriturePreviewResult {
  const [detail, setDetail] = useState<EcritureDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const open = useCallback(async (ecritureId: string) => {
    setLoading(true)
    setError(null)
    setDetail(null)
    try {
      const { caseList } = getContainer()
      const next = await caseList.getEcriture(ecritureId)
      setDetail(next)
    } catch (err: unknown) {
      const wrapped = err instanceof Error ? err : new Error(String(err))
      // eslint-disable-next-line no-console
      console.error('[useEcriturePreview] open failed', wrapped)
      setError(wrapped)
    } finally {
      setLoading(false)
    }
  }, [])

  const close = useCallback(() => {
    setDetail(null)
    setError(null)
    setLoading(false)
  }, [])

  return { detail, loading, error, open, close }
}
